import { ref } from 'vue';
import axios from 'axios';
import type { AvisoAppPendiente } from '@/interfaces/AvisoApp';

const POLL_MS = 60 * 60 * 1000;

const aviso = ref<AvisoAppPendiente | null>(null);
const isOpen = ref(false);
const loading = ref(false);
let intervalId: ReturnType<typeof setInterval> | null = null;
let checking = false;

function isAuthenticated(): boolean {
  return !!localStorage.getItem('token');
}

async function fetchPendiente(): Promise<void> {
  if (!isAuthenticated()) {
    resetAndStop();
    return;
  }
  if (checking) {
    return;
  }
  checking = true;
  try {
    const { data } = await axios.get<AvisoAppPendiente | null>('aviso-app/pendiente');
    if (!isAuthenticated()) {
      resetAndStop();
      return;
    }
    if (data && typeof data === 'object' && 'id' in data && isAuthenticated()) {
      aviso.value = data;
      isOpen.value = true;
    } else {
      aviso.value = null;
      isOpen.value = false;
    }
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      resetAndStop();
    }
  } finally {
    checking = false;
  }
}

function stopPolling(): void {
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

function startPolling(): void {
  if (!isAuthenticated()) {
    resetAndStop();
    return;
  }
  if (intervalId !== null) {
    return;
  }
  void fetchPendiente();
  intervalId = setInterval(() => {
    if (!isOpen.value) {
      void fetchPendiente();
    }
  }, POLL_MS);
}

function resetAndStop(): void {
  stopPolling();
  aviso.value = null;
  isOpen.value = false;
}

async function enviarRespuesta(payload: { boton?: number; texto?: string }): Promise<boolean> {
  if (!isAuthenticated() || !aviso.value) {
    resetAndStop();
    return false;
  }
  loading.value = true;
  try {
    await axios.post(`aviso-app/${aviso.value.id}/respuesta`, payload);
    aviso.value = null;
    isOpen.value = false;
    void fetchPendiente();
    return true;
  } catch {
    return false;
  } finally {
    loading.value = false;
  }
}

async function recordarMasTarde(): Promise<boolean> {
  if (!isAuthenticated() || !aviso.value) {
    resetAndStop();
    return false;
  }
  loading.value = true;
  try {
    await axios.post(`aviso-app/${aviso.value.id}/recordar-mas-tarde`);
    aviso.value = null;
    isOpen.value = false;
    return true;
  } catch {
    return false;
  } finally {
    loading.value = false;
  }
}

export function useAvisoApp() {
  return {
    aviso,
    isOpen,
    loading,
    fetchPendiente,
    startPolling,
    stopPolling,
    resetAndStop,
    enviarRespuesta,
    recordarMasTarde,
  };
}
